import axios from 'axios'
import { getStorage, ref, getDownloadURL, connectStorageEmulator } from 'firebase/storage'
import { useAuthStore } from '../stores/auth'
import { getAuthErrorHandler } from '@/utils/authErrorHandler'
import { reportError } from '@/utils/errorReporting'

const url = import.meta.env.VITE_API_URL

// Add request interceptor to automatically inject authentication headers
axios.interceptors.request.use(
  (config) => {
    // Auto-inject Authorization header for all other requests
    const authStore = useAuthStore()
    if (authStore.user?.accessToken) {
      config.headers.Authorization = `Bearer ${authStore.user.accessToken}`
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Add response interceptor to handle authentication errors globally
axios.interceptors.response.use(
  (response) => response, // Return successful responses as-is
  async (error) => {
    // Ignore axios cancellation errors (user navigated away, request aborted, etc.)
    if (axios.isCancel(error)) {
      console.log('Request canceled:', error.message)
      return Promise.reject(error)
    }

    // Ignore navigation cancellation errors (Vue Router, user navigating away)
    // These can appear as Error: cancel or similar messages
    /* if (error.message === 'cancel' || error.message === 'cancelled' || error.message === 'Navigation cancelled') {
      console.log('Navigation or operation canceled')
      return Promise.reject(error)
    } */

    // Handle 401 Unauthorized errors globally
    if (error.response?.status === 401) {
      const authHandler = getAuthErrorHandler()
      const canRetry = await authHandler.handleAuthError(error, 'API request')

      if (canRetry) {
        // Token was refreshed, retry the original request
        const originalRequest = error.config
        const authStore = useAuthStore()
        if (authStore.user?.accessToken) {
          originalRequest.headers.Authorization = `Bearer ${authStore.user.accessToken}`
          return axios.request(originalRequest)
        }
      }
    }

    // Report API errors to error reporting (except auth errors and cancellations)
    if (error.response?.status !== 401) {
      const context = {
        type: 'api-error',
        url: error.config?.url,
        method: error.config?.method,
        statusCode: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data
      }
      reportError(error, context)
    }

    return Promise.reject(error)
  }
)

let api = {
  hw: async (msg) => {
    const res = await axios.get(`${url}/hw?msg=${msg}`)
    return res.data
  },
  login: async (user, inviteToken, browserLang) => {
    let loginUrl = `${url}/login`
    const params = []
    if (inviteToken) {
      params.push(`invite=${encodeURIComponent(inviteToken)}`)
    }
    if (browserLang) {
      params.push(`lang=${encodeURIComponent(browserLang)}`)
    }
    if (params.length > 0) {
      loginUrl += `?${params.join('&')}`
    }
    return (await axios.post(loginUrl, user)).data
  },

  // createRide: async (ride, imageFile) => {
  //     const data = new FormData();
  //     if (imageFile) {
  //         data.append('image', imageFile, imageFile.name);
  //     }
  //     data.append('ride', new Blob([JSON.stringify(ride)], {type: "application/json"}));
  //     return (await axios.post(`${url}/ride/create`, data, {headers: {...getHeaders(), 'Content-Type': 'multipart/form-data'}})).data;
  // },
  // listRides: async () => {
  //     return await axios.get(`${url}/ride/list`, {headers: getHeaders()});
  // },

  createEvent: async (event, logo) => {
    const data = new FormData()
    if (logo) {
      data.append('logo', logo, logo.name)
    }
    data.append('event', new Blob([JSON.stringify(event)], { type: 'application/json' }))
    return (
      await axios.post(`${url}/event/create`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    ).data
  },
  updateEvent: async (event) => {
    return (await axios.patch(`${url}/event/${event.uid}`, event)).data
  },
  updateEventLogo: async (uid, logo) => {
    const data = new FormData()
    data.append('logo', logo, logo.name)
    return (await axios.patch(`${url}/event/${uid}/logo`, data)).data
  },
  listEvents: async () => {
    return (await axios.get(`${url}/event/list`)).data
  },
  listSubscriptions: async () => {
    return (await axios.get(`${url}/subscriptions/list`)).data
  },
  getSubscription: async (subscriptionUid) => {
    return (await axios.get(`${url}/subscriptions/${subscriptionUid}`)).data
  },
  getSubscriptionVehicles: async (subscriptionUid) => {
    return (await axios.get(`${url}/subscriptions/${subscriptionUid}/vehicles`)).data
  },
  checkExistingSubscription: async (eventUid) => {
    return (await axios.get(`${url}/subscriptions/exists/${eventUid}`)).data
  },
  getEvent: async (uid) => {
    return (await axios.get(`${url}/event/${uid}`)).data
  },
  getEventTaxNumbers: async (uid) => {
    return (await axios.get(`${url}/event/${uid}/taxNumbers`)).data
  },
  getEventSubscriptions: async (uid) => {
    return (await axios.get(`${url}/event/${uid}/subscriptions`)).data
  },
  getActivitySubscriptionCounts: async (uid) => {
    return (await axios.get(`${url}/event/${uid}/activities/subscriptions/count`)).data
  },
  getRouteAvailability: async (uid) => {
    return (await axios.get(`${url}/event/${uid}/activities/routes/availability`)).data
  },
  getTeamsSubscriptions: async (eventUid, activityUid) => {
    return (await axios.get(`${url}/event/${eventUid}/${activityUid}/team/subscriptions`)).data
  },
  reserveSubscription: async (eventUid, activityUid) => {
    return (await axios.get(`${url}/subscribe/${eventUid}/${activityUid}/reserve`)).data
  },
  reserveTeamSubscription: async (eventUid, teamUid) => {
    return (await axios.get(`${url}/subscribe/${eventUid}/team/${teamUid}/reserve`)).data
  },
  cancelReservations: async (eventUid) => {
    return (await axios.delete(`${url}/subscribe/${eventUid}/cancel`)).data
  },
  cancelActivityReservation: async (eventUid, activityUid) => {
    return (await axios.delete(`${url}/subscribe/${eventUid}/${activityUid}/cancel`)).data
  },
  // Inventory management
  initializeEventInventory: async (eventUid, allowUpdate = false) => {
    return (await axios.post(`${url}/event/${eventUid}/inventory/initialize`, { allowUpdate })).data
  },
  getInventoryStatus: async (eventUid) => {
    return (await axios.get(`${url}/event/${eventUid}/inventory/status`)).data
  },
  getFieldOptionInventory: async (eventUid, fieldUids = null) => {
    const params = fieldUids && fieldUids.length > 0 ? { fieldUids: fieldUids.join(',') } : {}
    return (await axios.get(`${url}/event/${eventUid}/inventory`, { params })).data
  },
  reserveFieldOptions: async (eventUid, selections) => {
    return (await axios.post(`${url}/event/${eventUid}/inventory/reserve`, { selections })).data
  },
  releaseFieldOptions: async (eventUid) => {
    return (await axios.post(`${url}/event/${eventUid}/inventory/release`, {})).data
  },
  deleteEvent: async (uid) => {
    return (await axios.delete(`${url}/event/${uid}`)).data
  },
  deleteActivity: async (eventUid, activityUid) => {
    return (await axios.delete(`${url}/event/${eventUid}/activity/${activityUid}`)).data
  },
  duplicateEvent: async (uid) => {
    return (await axios.post(`${url}/event/${uid}/duplicate`, {})).data
  },
  validatePromoCode: async (eventUid, promocode) => {
    return (await axios.get(`${url}/event/${eventUid}/promocode/${promocode}`)).data
  },
  payment: async (subscription) => {
    const eventUid = subscription.eventUid || subscription.event?.uid
    if (!eventUid) {
      throw new Error('Missing event identifier for payment')
    }
    return (await axios.post(`${url}/subscribe/${eventUid}/payment`, subscription)).data
  },
  fetchFile: async (key) => {
    const response = await axios.get(`${url}/file?key=${key}`, { responseType: 'blob' })
    return response.data
  },
  putFile: async (key, file) => {
    const data = new FormData()
    data.append('file', file, file.name)
    data.append('key', new Blob([key], { type: 'text/plain' }))
    return (
      await axios.post(`${url}/file`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
    ).data
  },
  deleteFile: async (key) => {
    return (await axios.delete(`${url}/file?key=${key}`)).data
  },
  exportActivitySubscriptions: async (eventUid, activityUid) => {
    const response = await axios.get(`${url}/event/${eventUid}/activity/${activityUid}/export`, {
      responseType: 'blob'
    })
    return response.data
  },
  exportEventSubscriptions: async (eventUid) => {
    const response = await axios.get(`${url}/event/${eventUid}/export`, {
      responseType: 'blob'
    })
    return response.data
  },
  updateSubscription: async (subscriptionUid, data) => {
    return (await axios.patch(`${url}/subscriptions/${subscriptionUid}`, data)).data
  },
  deleteSubscription: async (subscriptionUid) => {
    return (await axios.delete(`${url}/subscriptions/${subscriptionUid}`)).data
  },
  removeActivityFromSubscription: async (subscriptionUid, activityUid) => {
    return (await axios.delete(`${url}/subscriptions/${subscriptionUid}/activity/${activityUid}`))
      .data
  },
  updateAccount: async (account) => {
    return (await axios.patch(`${url}/account`, account)).data
  },
  getVehicles: async () => {
    const result = await axios.get(`${url}/vehicles`)
    return result.data
  },
  createVehicle: async (vehicle) => {
    const result = await axios.post(`${url}/vehicles`, vehicle)
    return result.data
  },
  updateVehicle: async (vehicleUid, vehicle) => {
    const result = await axios.patch(`${url}/vehicles/${vehicleUid}`, vehicle)
    return result.data
  },
  deleteVehicle: async (vehicleUid) => {
    const result = await axios.delete(`${url}/vehicles/${vehicleUid}`)
    return result.data
  },
  getStripeConnectUrl: async () => {
    return (await axios.get(`${url}/stripe/connect/start`)).data
  },
  stripeDisconnect: async () => {
    return (await axios.post(`${url}/stripe/connect/disconnect`, {})).data
  },
  configureStripeKeys: async (secretKey, publishableKey) => {
    return (await axios.post(`${url}/stripe/configure-key`, { secretKey, publishableKey })).data
  },
  removeStripeKeys: async () => {
    return (await axios.post(`${url}/stripe/remove-key`, {})).data
  },
  listPaymentProfiles: async () => {
    return (await axios.get(`${url}/stripe/payment-profiles`)).data
  },
  createPaymentProfile: async ({ label, secretKey, publishableKey, isDefault = false }) => {
    return (
      await axios.post(`${url}/stripe/payment-profiles`, {
        label,
        secretKey,
        publishableKey,
        isDefault
      })
    ).data
  },
  updatePaymentProfile: async (profileUid, { label, secretKey, publishableKey, isDefault }) => {
    return (
      await axios.patch(`${url}/stripe/payment-profiles/${profileUid}`, {
        label,
        secretKey,
        publishableKey,
        isDefault
      })
    ).data
  },
  setDefaultPaymentProfile: async (profileUid) => {
    return (await axios.post(`${url}/stripe/payment-profiles/${profileUid}/default`, {})).data
  },
  deletePaymentProfile: async (profileUid) => {
    return (await axios.delete(`${url}/stripe/payment-profiles/${profileUid}`)).data
  },
  getFileDownloadUrl: async (key) => {
    if (!key) {
      return ''
    }
    const storage = getStorage()
    if (location.hostname === 'localhost') {
      // Point to the Storage emulator running on localhost.
      connectStorageEmulator(storage, '127.0.0.1', 9199)
    }
    const fileRef = ref(storage, key)
    try {
      return await getDownloadURL(fileRef)
    } catch (error) {
      if (error?.code === 'storage/object-not-found') {
        return ''
      }
      throw error
    }
  },

  // Root Admin API
  rootAdmin: {
    listAccounts: async (filters = {}) => {
      const params = new URLSearchParams()
      if (filters.isAdmin !== undefined) {
        params.append('isAdmin', filters.isAdmin)
      }
      if (filters.emailSearch) {
        params.append('emailSearch', filters.emailSearch)
      }
      if (filters.lastActiveDays) {
        params.append('lastActiveDays', filters.lastActiveDays)
      }
      const queryString = params.toString()
      const fullUrl = queryString
        ? `${url}/root-admin/accounts?${queryString}`
        : `${url}/root-admin/accounts`
      return (await axios.get(fullUrl)).data
    },
    impersonateUser: async (targetUid) => {
      return (await axios.post(`${url}/root-admin/impersonate`, { targetUid })).data
    },
    createAdminInvite: async (invitedEmail) => {
      return (await axios.post(`${url}/root-admin/admin-invites`, { invitedEmail })).data
    },
    exitImpersonation: async (rootUid) => {
      return (await axios.post(`${url}/root-admin/exit-impersonation`, { rootUid })).data
    }
  },

  // Leader Invites API
  leaderInvites: {
    create: async (eventUid, inviteData) => {
      return (await axios.post(`${url}/event/${eventUid}/collaborators/leader-invites`, inviteData))
        .data
    },
    list: async (eventUid) => {
      return (await axios.get(`${url}/event/${eventUid}/collaborators/leader-invites`)).data
    },
    revoke: async (eventUid, token) => {
      return (await axios.delete(`${url}/event/${eventUid}/collaborators/invites/${token}`)).data
    },
    resend: async (eventUid, token) => {
      return (await axios.post(`${url}/event/${eventUid}/collaborators/invites/${token}/resend`))
        .data
    },
    validate: async (token) => {
      return (await axios.get(`${url}/invite/${token}/validate`)).data
    },
    accept: async (token) => {
      return (await axios.post(`${url}/invite/${token}/accept`, {})).data
    }
  },

  // Teams API
  teams: {
    create: async (eventUid, activityUid, teamData) => {
      return (
        await axios.post(`${url}/event/${eventUid}/activities/${activityUid}/teams`, teamData)
      ).data
    },
    getMyTeams: async () => {
      return (await axios.get(`${url}/teams/my-teams`)).data
    },
    getMembers: async (eventUid, teamUid) => {
      return (await axios.get(`${url}/event/${eventUid}/teams/${teamUid}/members`)).data
    },
    getMemberSubscription: async (eventUid, teamUid, memberUid) => {
      return (
        await axios.get(
          `${url}/event/${eventUid}/teams/${teamUid}/members/${memberUid}/subscription`
        )
      ).data
    },
    update: async (eventUid, teamUid, updates) => {
      return (await axios.patch(`${url}/event/${eventUid}/teams/${teamUid}`, updates)).data
    }
  },

  // Account search for autocomplete
  searchAccounts: async (searchTerm) => {
    return (await axios.get(`${url}/accounts/search?q=${encodeURIComponent(searchTerm)}`)).data
  }
}

export default api
